import styled from 'styled-components'
import { Colors } from 'src/constants/colors'
import React, { useEffect, useState } from 'react'
import { FjText } from 'src/components/Common'
import { camelToSentenceCase } from 'src/utils/format'
import { Tabs, TabsProps } from 'antd'
import { getQueryParam } from 'src/utils/urlParams'
import { sharedAppStateStore } from 'src/store/AppStateStore'

export const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.sharkOpacity12};
  border-radius: 8px;
  overflow: hidden;
  background: ${Colors.white};
  & > *:not(:last-child) .category-item,
  & .category-item:not(:last-child) {
    border-bottom: 1px solid ${Colors.sharkOpacity10};
  }
`

const CategoryContainer = styled.div<{ isSubTab: boolean }>`
  position: relative;
  background: ${Colors.white};
  padding: 12px 16px 12px 24px;
  cursor: pointer;
  transition: 0.2s;
  padding-left: ${(props) => (props.isSubTab ? 'calc(7% + 24px)' : '24px')};
  text-align: left;

  :hover {
    background: ${Colors.hawkesBlue};
  }
`

const CategoryHighlight = styled.div<{ isSubTab: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 7px;
  height: 100%;
  background-color: ${Colors.texasRose};
  margin-left: ${(props) => (props.isSubTab ? '20px' : '0')};
`

export const CategoryItem: React.FC<{
  category: string | React.ReactNode
  active?: boolean
  onClick?: () => void
  isSubTab?: boolean
  containsSubCategory?: boolean
}> = ({ category, active, onClick, isSubTab }) => {
  return (
    <CategoryContainer onClick={onClick} isSubTab={isSubTab} className="category-item">
      {active && <CategoryHighlight isSubTab={isSubTab} />}
      <FjText fontSize="14px" color={Colors.chambray} fontWeight="bold" textTransform="capitalize" textAlign="left">
        {typeof category === 'string' ? camelToSentenceCase(category) : category}
      </FjText>
    </CategoryContainer>
  )
}

interface QueryParamTabsProps extends Omit<TabsProps, 'defaultActiveKey' | 'onChange'> {
  defaultActiveKey: string
  items: (TabsProps['items'][number] | null)[]
  getNavigationPath: (tab: string) => string
  tabQueryKey?: string
}

export const QueryParamTabs: React.FC<QueryParamTabsProps> = ({
  defaultActiveKey,
  items,
  getNavigationPath,
  tabQueryKey = 'tab',
  activeKey,
  ...tabsProps
}) => {
  const [activeTabKey, setActiveTabKey] = useState(activeKey || defaultActiveKey)
  const lastRoute = sharedAppStateStore.routes[sharedAppStateStore.routes.length - 1]

  useEffect(() => {
    if (items.length === 0) return
    let tab = getQueryParam(tabQueryKey)
    if (tab === activeKey) return
    const validTabKeys = items.filter((item) => item != null).map((item) => item.key as string)

    if (!validTabKeys.includes(tab)) {
      tab = defaultActiveKey
    }
    setActiveTabKey(tab)
    sharedAppStateStore.navigate(getNavigationPath(tab), true, lastRoute)
    // Excluding lastRoute from deps as it's derived during render and will always be up-to-date within the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultActiveKey, items, getNavigationPath, tabQueryKey])

  const handleTabChange = (tabKey: string) => {
    setActiveTabKey(tabKey)
    sharedAppStateStore.navigate(getNavigationPath(tabKey), false, lastRoute)
  }

  return <Tabs {...tabsProps} activeKey={activeTabKey} onChange={handleTabChange} items={items} />
}
